import type { ChartData, ChartOptions } from 'chart.js';
import type { Dayjs } from 'dayjs';
import { type CSSProperties, useMemo } from 'react';
import { Line } from 'react-chartjs-2';

import useColor from '@/hooks/domain/useColor';
import {
  CUSTOMER_QUERY_DATE_RANGE,
  CUSTOMER_QUERY_SENTIMENT,
} from '@/types/customerConst';
import { getTimezoneDayjs } from '@/utils/time';

type Props = {
  data: SentimentDistItemT[];
  chartStyle?: CSSProperties;
  startDate: Dayjs;
  endDate: Dayjs;
  dateRange: CUSTOMER_QUERY_DATE_RANGE;
  sentiment?: CUSTOMER_QUERY_SENTIMENT;
};

class LabelData {
  data: SentimentDistItemT[];

  timestamp: Dayjs;
  label: string;

  constructor(timestamp: Dayjs, label: string) {
    this.data = [];
    this.timestamp = timestamp;
    this.label = label;
  }
}

export const SentimentTrendLineChart = (props: Props) => {
  const color = useColor();
  const dateLabels = useMemo(() => {
    const labelDatas: LabelData[] = [];

    let currDate = props.startDate;

    switch (props.dateRange) {
      case CUSTOMER_QUERY_DATE_RANGE.DAY1:
        // Group totalDuration by 2 hours
        while (currDate.isBefore(props.endDate)) {
          const label = currDate.format('HH:mm');
          labelDatas.push(new LabelData(currDate, label));
          currDate = currDate.addVal(2, 'hour');
        }
        break;
      case CUSTOMER_QUERY_DATE_RANGE.DAY7:
        // Group totalDuration by day
        while (currDate.isBefore(props.endDate)) {
          const label = currDate.format('MMM DD');
          labelDatas.push(new LabelData(currDate, label));
          currDate = currDate.addVal(1, 'day');
        }
        break;
      case CUSTOMER_QUERY_DATE_RANGE.DAY30:
        // Group totalDuration by 7 days
        while (currDate.isBefore(props.endDate)) {
          const label = currDate.format('MMM DD');
          labelDatas.push(new LabelData(currDate, label));
          currDate = currDate.addVal(1, 'day');
        }
        break;
      case CUSTOMER_QUERY_DATE_RANGE.DAY90:
        // Group totalDuration by 7 days
        while (currDate.isBefore(props.endDate)) {
          const label = currDate.format('MMM DD');
          labelDatas.push(new LabelData(currDate, label));
          currDate = currDate.addVal(1, 'day');
        }
        break;
    }

    props.data.forEach((dataItem) => {
      const currDate = getTimezoneDayjs(dataItem.date);

      // find the closest date label
      const closestLabel = labelDatas.reduce((acc, curr) =>
        Math.abs(curr.timestamp.diff(currDate)) <
        Math.abs(acc.timestamp.diff(currDate))
          ? curr
          : acc,
      );

      closestLabel.data.push(dataItem);
    });

    return labelDatas;
  }, [props.startDate, props.endDate, props.dateRange, props.data]);

  const chartDatasets: ChartData<'line'>['datasets'] = useMemo(() => {
    const datasets: ChartData<'line'>['datasets'] = [];

    if (props.sentiment) {
      datasets.push({
        fill: true,
        tension: 0.4,
        label:
          props.sentiment === CUSTOMER_QUERY_SENTIMENT.HAPPY
            ? 'Happy'
            : 'Unhappy',
        data: dateLabels.map((data) => {
          return data.data.filter((dataItem) => {
            switch (props.sentiment) {
              case CUSTOMER_QUERY_SENTIMENT.HAPPY:
                return dataItem.value === 1 || dataItem.value === 4;
              case CUSTOMER_QUERY_SENTIMENT.UNHAPPY:
                return dataItem.value === 3 || dataItem.value === 5;
            }
          }).length;
        }),
        borderColor:
          props.sentiment === CUSTOMER_QUERY_SENTIMENT.HAPPY
            ? color.green500
            : color.red600,
        backgroundColor: (context) => {
          const bgColor =
            props.sentiment === CUSTOMER_QUERY_SENTIMENT.HAPPY
              ? ['rgba(0, 255, 0, 0.55)', 'rgba(218, 222, 255, 0)']
              : ['rgba(255, 0, 0, 0.55)', 'rgba(218, 222, 255, 0)'];

          if (!context.chart.chartArea) {
            return;
          }

          const {
            ctx,
            chartArea: { top, bottom },
          } = context.chart;
          const gradientBg = ctx.createLinearGradient(0, top, 0, bottom);

          gradientBg.addColorStop(0.2, bgColor[0]);
          gradientBg.addColorStop(1, bgColor[1]);

          return gradientBg;
        },
        borderWidth: 1,
        pointRadius: 0,
      });
    } else {
      // add both happy and unhappy
      datasets.push({
        fill: true,
        tension: 0.4,
        label: 'Happy',
        data: dateLabels.map((data) => {
          return data.data.filter((dataItem) => {
            return dataItem.value === 1 || dataItem.value === 4;
          }).length;
        }),
        borderColor: color.green500,
        backgroundColor: (context) => {
          const bgColor = ['rgba(0, 255, 0, 0.55)', 'rgba(218, 222, 255, 0)'];

          if (!context.chart.chartArea) {
            return;
          }

          const {
            ctx,
            chartArea: { top, bottom },
          } = context.chart;
          const gradientBg = ctx.createLinearGradient(0, top, 0, bottom);

          gradientBg.addColorStop(0.2, bgColor[0]);
          gradientBg.addColorStop(1, bgColor[1]);

          return gradientBg;
        },
        borderWidth: 1,
        pointRadius: 0,
      });

      datasets.push({
        fill: true,
        tension: 0.4,
        label: 'Unhappy',
        data: dateLabels.map((data) => {
          return data.data.filter((dataItem) => {
            return dataItem.value === 3 || dataItem.value === 5;
          }).length;
        }),
        borderColor: color.red600,
        backgroundColor: (context) => {
          const bgColor = ['rgba(255, 0, 0, 0.55)', 'rgba(218, 222, 255, 0)'];

          if (!context.chart.chartArea) {
            return;
          }

          const {
            ctx,
            chartArea: { top, bottom },
          } = context.chart;
          const gradientBg = ctx.createLinearGradient(0, top, 0, bottom);

          gradientBg.addColorStop(0.2, bgColor[0]);
          gradientBg.addColorStop(1, bgColor[1]);

          return gradientBg;
        },
        borderWidth: 1,
        pointRadius: 0,
      });
    }

    datasets.push({
      fill: true,
      tension: 0.4,
      label: 'Total',
      data: dateLabels.map((label) => label.data.length),
      borderColor: color.gray400,
      backgroundColor: (context) => {
        const bgColor = ['rgba(185, 185, 185, 0.55)', 'rgba(218, 222, 255, 0)'];

        if (!context.chart.chartArea) {
          return;
        }

        const {
          ctx,
          chartArea: { top, bottom },
        } = context.chart;
        const gradientBg = ctx.createLinearGradient(0, top, 0, bottom);

        gradientBg.addColorStop(0.2, bgColor[0]);
        gradientBg.addColorStop(1, bgColor[1]);

        return gradientBg;
      },
      borderWidth: 1,
      pointRadius: 0,
    });
    return datasets;
  }, [
    props.sentiment,
    color.green500,
    color.red600,
    color.gray400,
    dateLabels,
  ]);

  const chartData: ChartData<'line'> = {
    labels: dateLabels.map((label, index) => {
      switch (props.dateRange) {
        case CUSTOMER_QUERY_DATE_RANGE.DAY1:
          if (index % 12 === 0) {
            return label.timestamp.format('MMM DD');
          }
          return '';
        case CUSTOMER_QUERY_DATE_RANGE.DAY7:
          if (index % 2 === 0) {
            return label.timestamp.format('MMM DD');
          }
          return '';
        case CUSTOMER_QUERY_DATE_RANGE.DAY30:
          if (index % 4 === 0) {
            return label.timestamp.format('MMM DD');
          }
          return '';
        case CUSTOMER_QUERY_DATE_RANGE.DAY90:
          if (index % 15 === 0) {
            return label.timestamp.format('MMM DD');
          }
          return '';
        default:
          return label.label;
      }
    }),
    datasets: chartDatasets,
  };

  const chartOptions: ChartOptions<'line'> = {
    animation: false,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        display: false,
      },
      tooltip: {
        mode: 'nearest',
        intersect: false,
        enabled: true,
      },
    },
    scales: {
      y: {
        ticks: {
          precision: 0,
          font: {
            family: 'Instrument Sans',
          },
        },
        min: 0,
      },
      x: {
        ticks: {
          font: {
            family: 'Instrument Sans',
          },
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'nearest',
      axis: 'xy',
    },
  };

  return (
    <div style={props.chartStyle}>
      <Line options={chartOptions} data={chartData} />
    </div>
  );
};
